import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV_LINKS, openWhatsApp, GENERAL_WHATSAPP_MESSAGE } from "@/lib/constants";
import { scrollToId, lenisRef } from "@/lib/lenis";
import { EASE } from "@/components/Reveal";
import OpenBadge from "@/components/OpenBadge";

export default function Header({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const lenis = lenisRef.current;
    if (!lenis) return;
    if (menuOpen) lenis.stop();
    else lenis.start();
  }, [menuOpen]);

  const go = (id) => {
    setMenuOpen(false);
    if (lenisRef.current) lenisRef.current.start();
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled
          ? "border-[#D4AF37]/20 bg-[#0E1420]/90 shadow-[0_12px_40px_rgba(0,0,0,0.35)]"
          : "border-[#D4AF37]/15 bg-[#0E1420]/80"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="group flex items-center gap-3"
        >
          <span className="font-display text-[1.7rem] font-semibold leading-none tracking-tight text-[#F9F6F0] transition-colors duration-300 group-hover:text-[#F3E5AB]">
            R<span className="text-[#D4AF37]">&amp;</span>J
          </span>
          <span className="h-7 w-px bg-[#D4AF37]/40" />
          <span className="text-[9px] font-medium uppercase leading-[1.6] tracking-[0.32em] text-[#A8B0BC]">
            Dry
            <br />
            Cleaners
          </span>
        </button>

        <nav className="hidden items-center gap-6 xl:flex" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-link-${link.id}`}
              onClick={() => go(link.id)}
              className="group relative px-2 py-2 text-[11px] font-medium uppercase tracking-[0.2em] text-[#B9C1CE] transition-colors duration-300 hover:text-white"
            >
              {link.label}
              <span className="absolute inset-x-2 bottom-0 h-px origin-left scale-x-0 bg-[#D4AF37] transition-transform duration-500 ease-out group-hover:scale-x-100" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <OpenBadge className="hidden xl:inline-flex" />
          <button
            data-testid="header-whatsapp-btn"
            onClick={() => openWhatsApp(GENERAL_WHATSAPP_MESSAGE)}
            className="flex items-center gap-2 border border-[#D4AF37]/40 px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] text-[#D4AF37] transition-[background-color,border-color] duration-300 hover:border-[#D4AF37]/80 hover:bg-[#D4AF37]/10"
          >
            <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
            WhatsApp Us
          </button>
          <button
            data-testid="header-book-pickup-btn"
            onClick={onBook}
            className="group relative overflow-hidden bg-[#D4AF37] px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0E1420] transition-colors duration-300 hover:bg-[#F3E5AB]"
          >
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
            Book a Pickup
          </button>
        </div>

        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37] xl:hidden"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
        </button>
      </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-40 overflow-y-auto bg-[#0B101A]/95 pt-[72px] backdrop-blur-2xl xl:hidden"
          >
            <div className="flex min-h-full flex-col justify-between px-6 pb-10 pt-8">
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((link, i) => (
                  <motion.button
                    key={link.id}
                    data-testid={`mobile-nav-link-${link.id}`}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 * i + 0.1, duration: 0.6, ease: EASE }}
                    onClick={() => go(link.id)}
                    className="border-b border-[#D4AF37]/10 py-4 text-left font-display text-3xl font-light text-[#F9F6F0] transition-colors duration-300 hover:text-[#D4AF37]"
                  >
                    {link.label}
                  </motion.button>
                ))}
              </nav>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6, ease: EASE }}
                className="mt-10 flex flex-col gap-3"
              >
                <div className="flex justify-center pb-1">
                  <OpenBadge />
                </div>
                <button
                  data-testid="mobile-book-pickup-btn"
                  onClick={() => {
                    setMenuOpen(false);
                    onBook();
                  }}
                  className="w-full bg-[#D4AF37] py-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#0B101A]"
                >
                  Book a Pickup
                </button>
                <button
                  data-testid="mobile-whatsapp-btn"
                  onClick={() => openWhatsApp(GENERAL_WHATSAPP_MESSAGE)}
                  className="flex w-full items-center justify-center gap-2 border border-[#D4AF37]/40 py-4 text-xs font-medium uppercase tracking-[0.25em] text-[#D4AF37]"
                >
                  <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
                  WhatsApp Us
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
